import React, { useEffect, useRef, useState } from "react";
import InputBox from "../../../business-types/grocery/components/InputBox";

type BarcodeScannerProps = {
  onDetected: (result: string) => void;
};

const SCAN_TIMEOUT = 50;

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onDetected }) => {
  const [manualCode, setManualCode] = useState("");
  const [error, setError] = useState("");

  const bufferRef = useRef("");
  const lastKeyTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      // Ignore typing inside inputs
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA"
      ) {
        return;
      }

      const now = Date.now();

      // Reset buffer if typing is slow
      if (now - lastKeyTimeRef.current > SCAN_TIMEOUT) {
        bufferRef.current = "";
      }

      lastKeyTimeRef.current = now;

      if (e.key === "Enter") {
        const code = bufferRef.current.trim();

        if (code.length > 3) {
          setError("");
          onDetected(code);
        } else if (code.length > 0) {
          setError("Invalid barcode. Barcode must contain at least 4 characters.");
        }

        bufferRef.current = "";
        return;
      }

      // Accept only letters and numbers
      if (/^[a-zA-Z0-9]$/.test(e.key)) {
        bufferRef.current += e.key;
        setError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onDetected]);

  const handleManualChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Check for spaces or special characters
    if (/[^a-zA-Z0-9]/.test(value)) {
      setError(
        "Invalid barcode. Only letters and numbers are allowed."
      );
    } else {
      setError("");
    }

    // Remove spaces and special characters
    const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, "");

    setManualCode(cleanedValue);
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim();

    if (!code) {
      setError("Please enter a barcode.");
      return;
    }

    if (code.length < 4) {
      setError("Invalid barcode. Barcode must contain at least 4 characters.");
      return;
    }

    setError("");
    onDetected(code);
    setManualCode("");
  };

  return (
    <div className="flex flex-col items-center gap-1 m-2">
      <div className="flex items-center gap-2">
        <InputBox
          type="text"
          placeholder="Enter barcode manually"
          value={manualCode}
          onChange={handleManualChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleManualSubmit();
            }
          }}
        />
      </div>

      {error && (
        <p className="text-sm text-red-500" >
          {error}
        </p>
      )}
    </div>
  );
};

export default BarcodeScanner;